import React, { useState, useEffect, useMemo } from 'react'

import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormControlLabel,
  Switch,
  Button,
  Divider,
  Stack,
  TextField,
  Autocomplete,
  IconButton,
} from '@mui/material'
import { AutocompleteChangeReason } from '@mui/material/Autocomplete'

import { SelectChangeEvent } from '@mui/material/Select'

import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import { ProjectFilter, Metal, ProjectStage, Watchlist } from '@/types'
import { ISO_COUNTRY_CODES, IsoCountryCode } from '@/constants/countries'

import { GET_USER_WATCHLISTS } from '@/graphql/queries'
import { useAuth } from '@/hooks/useAuth'
import FilterListIcon from '@mui/icons-material/FilterList'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'

type FilterType = 'projectName' | 'operator' | 'mineralResource' | 'annualProduction' | 'metals' | 'stages' | 'countries' | 'watchlist' | ''

interface ProjectFiltersProps {
  onFilterChange?: (filter: ProjectFilter) => void
  isOpen?: boolean
  onToggle?: () => void
}

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

export const ProjectFilters: React.FC<ProjectFiltersProps> = ({
  onFilterChange,
  isOpen: _isOpen,
  onToggle: _onToggle,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()

  const [selectedMetals, setSelectedMetals] = useState<Metal[]>([])
  const [selectedStages, setSelectedStages] = useState<ProjectStage[]>([])
  const [selectedCountries, setSelectedCountries] = useState<IsoCountryCode[]>([])
  const [favoritesOnly, setFavoritesOnly] = useState(false)
  const [selectedWatchlist, setSelectedWatchlist] = useState<string>('')
  const [countrySearch, setCountrySearch] = useState('')
  const [projectNameInput, setProjectNameInput] = useState('')
  const [projectNameFilter, setProjectNameFilter] = useState('')
  const [operatorInput, setOperatorInput] = useState('')
  const [operatorFilter, setOperatorFilter] = useState('')
  const [resourceMetal, setResourceMetal] = useState<Metal | ''>('')
  const [resourceMinInput, setResourceMinInput] = useState('')
  const [resourceMaxInput, setResourceMaxInput] = useState('')
  const [resourceFilter, setResourceFilter] = useState<{ metal: Metal; minContent?: number; maxContent?: number } | null>(null)
  const [productionMetal, setProductionMetal] = useState<Metal | ''>('')
  const [productionMinInput, setProductionMinInput] = useState('')
  const [productionMaxInput, setProductionMaxInput] = useState('')
  const [productionFilter, setProductionFilter] = useState<{ metal: Metal; minProduction?: number; maxProduction?: number } | null>(null)

  // Filter type selector state
  const [activeFilterType, setActiveFilterType] = useState<FilterType>('')

  // Get user's watchlists
  const { data: watchlistsData } = useQuery(GET_USER_WATCHLISTS, {
    variables: { userId: user?.id },
    skip: !user?.id,
  })

  const watchlists: Watchlist[] = watchlistsData?.getUserWatchlists || []

  const metals = Object.values(Metal)
  const stages = Object.values(ProjectStage)
  const countryOptions = useMemo(
    () =>
      ISO_COUNTRY_CODES.map((code) => ({
        code,
        label: t(`projects.countries.${code}`),
      })),
    [t]
  )

  useEffect(() => {
    const filter: ProjectFilter = {
      metals: selectedMetals.length > 0 ? selectedMetals : undefined,
      stages: selectedStages.length > 0 ? selectedStages : undefined,
      countries: selectedCountries.length > 0 ? selectedCountries : undefined,
      name: projectNameFilter || undefined,
      operator: operatorFilter || undefined,
      mineralResource: resourceFilter || undefined,
      annualProduction: productionFilter || undefined,
      favoritesOnly: favoritesOnly || undefined,
      watchlistId: selectedWatchlist || undefined,
    }

    onFilterChange?.(filter)
  }, [selectedMetals, selectedStages, selectedCountries, favoritesOnly, selectedWatchlist, projectNameFilter, operatorFilter, resourceFilter, productionFilter, onFilterChange])

  const handleMetalChange = (event: SelectChangeEvent<typeof selectedMetals>) => {
    const value = event.target.value
    setSelectedMetals(typeof value === 'string' ? value.split(',') as Metal[] : value)
  }

  const handleStageChange = (event: SelectChangeEvent<typeof selectedStages>) => {
    const value = event.target.value
    setSelectedStages(typeof value === 'string' ? value.split(',') as ProjectStage[] : value)
  }

  const handleClearAll = () => {
    setSelectedMetals([])
    setSelectedStages([])
    setSelectedCountries([])
    setFavoritesOnly(false)
    setSelectedWatchlist('')
    setCountrySearch('')
    setProjectNameInput('')
    setProjectNameFilter('')
    setOperatorInput('')
    setOperatorFilter('')
    setResourceMetal('')
    setResourceMinInput('')
    setResourceMaxInput('')
    setResourceFilter(null)
    setProductionMetal('')
    setProductionMinInput('')
    setProductionMaxInput('')
    setProductionFilter(null)
  }

  const hasActiveFilters = selectedMetals.length > 0 ||
                          selectedStages.length > 0 ||
                          selectedCountries.length > 0 ||
                          projectNameFilter !== '' ||
                          operatorFilter !== '' ||
                          resourceFilter !== null ||
                          productionFilter !== null ||
                          favoritesOnly ||
                          selectedWatchlist !== ''

  const handleProjectNameKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      setProjectNameFilter(projectNameInput.trim())
    }
  }

  const handleProjectNameChipDelete = () => {
    setProjectNameFilter('')
    setProjectNameInput('')
  }

  const handleOperatorKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      setOperatorFilter(operatorInput.trim())
    }
  }

  const handleOperatorChipDelete = () => {
    setOperatorFilter('')
    setOperatorInput('')
  }

  const handleResourceKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (!resourceMetal) {
        return
      }

      const minVal = resourceMinInput.trim() !== '' ? Number(resourceMinInput.replace(/,/g, '.')) : undefined
      const maxVal = resourceMaxInput.trim() !== '' ? Number(resourceMaxInput.replace(/,/g, '.')) : undefined

      if (minVal === undefined && maxVal === undefined) {
        return
      }

      if ((minVal !== undefined && (Number.isNaN(minVal) || minVal < 0)) ||
          (maxVal !== undefined && (Number.isNaN(maxVal) || maxVal < 0))) {
        return
      }

      const filter: { metal: Metal; minContent?: number; maxContent?: number } = { metal: resourceMetal }
      if (minVal !== undefined) filter.minContent = minVal
      if (maxVal !== undefined) filter.maxContent = maxVal
      setResourceFilter(filter)
    }
  }

  const handleResourceChipDelete = () => {
    setResourceFilter(null)
    setResourceMetal('')
    setResourceMinInput('')
    setResourceMaxInput('')
  }

  const handleProductionKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (!productionMetal) {
        return
      }

      const minVal = productionMinInput.trim() !== '' ? Number(productionMinInput.replace(/,/g, '.')) : undefined
      const maxVal = productionMaxInput.trim() !== '' ? Number(productionMaxInput.replace(/,/g, '.')) : undefined

      if (minVal === undefined && maxVal === undefined) {
        return
      }

      if ((minVal !== undefined && (Number.isNaN(minVal) || minVal < 0)) ||
          (maxVal !== undefined && (Number.isNaN(maxVal) || maxVal < 0))) {
        return
      }

      const filter: { metal: Metal; minProduction?: number; maxProduction?: number } = { metal: productionMetal }
      if (minVal !== undefined) filter.minProduction = minVal
      if (maxVal !== undefined) filter.maxProduction = maxVal
      setProductionFilter(filter)
    }
  }

  const handleProductionChipDelete = () => {
    setProductionFilter(null)
    setProductionMetal('')
    setProductionMinInput('')
    setProductionMaxInput('')
  }

  const filterTypeOptions: { value: FilterType; label: string }[] = [
    { value: 'projectName', label: t('projects.filters.projectName') },
    { value: 'operator', label: t('projects.filters.operator') },
    { value: 'mineralResource', label: t('projects.filters.mineralResource') },
    { value: 'annualProduction', label: t('projects.filters.annualProduction') },
    { value: 'metals', label: t('projects.filters.metals') },
    { value: 'stages', label: t('projects.filters.stages') },
    { value: 'countries', label: t('projects.filters.countries') },
    ...(watchlists.length > 0 ? [{ value: 'watchlist' as FilterType, label: t('projects.filters.watchlist') }] : []),
  ]

  const handleCancelFilter = () => {
    setActiveFilterType('')
  }

  const renderFilterForm = () => {
    switch (activeFilterType) {
      case 'projectName':
        return (
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">{t('projects.filters.projectName')}</Typography>
              <IconButton size="small" onClick={handleCancelFilter}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder={t('projects.filters.projectNamePlaceholder')}
              value={projectNameInput}
              onChange={(event) => setProjectNameInput(event.target.value)}
              onKeyDown={(e) => { handleProjectNameKeyDown(e); if (e.key === 'Enter') setActiveFilterType('') }}
              autoFocus
            />
          </Box>
        )
      case 'operator':
        return (
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">{t('projects.filters.operator')}</Typography>
              <IconButton size="small" onClick={handleCancelFilter}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder={t('projects.filters.operatorPlaceholder')}
              value={operatorInput}
              onChange={(event) => setOperatorInput(event.target.value)}
              onKeyDown={(e) => { handleOperatorKeyDown(e); if (e.key === 'Enter') setActiveFilterType('') }}
              autoFocus
            />
          </Box>
        )
      case 'mineralResource':
        return (
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">{t('projects.filters.mineralResource')}</Typography>
              <IconButton size="small" onClick={handleCancelFilter}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            <Stack spacing={1}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('projects.filters.mineralResourceMetal')}</InputLabel>
                <Select
                  value={resourceMetal}
                  onChange={(event) => setResourceMetal(event.target.value as Metal | '')}
                  label={t('projects.filters.mineralResourceMetal')}
                >
                  <MenuItem value=""><em>{t('common.select')}</em></MenuItem>
                  {([Metal.AU, Metal.AG, Metal.CU] as Metal[]).map((metal) => (
                    <MenuItem key={metal} value={metal}>{t(`projects.metals.${metal}`)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction="row" spacing={1}>
                <TextField
                  sx={{ flex: 1 }}
                  size="small"
                  label={t('projects.filters.mineralResourceMinAmount')}
                  placeholder={t('projects.filters.mineralResourceAmountPlaceholder')}
                  value={resourceMinInput}
                  onChange={(event) => setResourceMinInput(event.target.value)}
                  onKeyDown={(e) => { handleResourceKeyDown(e); if (e.key === 'Enter' && resourceMetal) setActiveFilterType('') }}
                  type="number"
                  inputProps={{ min: 0 }}
                />
                <TextField
                  sx={{ flex: 1 }}
                  size="small"
                  label={t('projects.filters.mineralResourceMaxAmount')}
                  placeholder={t('projects.filters.mineralResourceAmountPlaceholder')}
                  value={resourceMaxInput}
                  onChange={(event) => setResourceMaxInput(event.target.value)}
                  onKeyDown={(e) => { handleResourceKeyDown(e); if (e.key === 'Enter' && resourceMetal) setActiveFilterType('') }}
                  type="number"
                  inputProps={{ min: 0 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">{t('projects.filters.mineralResourceHelper')}</Typography>
            </Stack>
          </Box>
        )
      case 'annualProduction':
        return (
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">{t('projects.filters.annualProduction')}</Typography>
              <IconButton size="small" onClick={handleCancelFilter}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            <Stack spacing={1}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('projects.filters.annualProductionMetal')}</InputLabel>
                <Select
                  value={productionMetal}
                  onChange={(event) => setProductionMetal(event.target.value as Metal | '')}
                  label={t('projects.filters.annualProductionMetal')}
                >
                  <MenuItem value=""><em>{t('common.select')}</em></MenuItem>
                  {([Metal.AU, Metal.AG, Metal.CU] as Metal[]).map((metal) => (
                    <MenuItem key={metal} value={metal}>{t(`projects.metals.${metal}`)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction="row" spacing={1}>
                <TextField
                  sx={{ flex: 1 }}
                  size="small"
                  label={t('projects.filters.annualProductionMinAmount')}
                  placeholder={t('projects.filters.annualProductionAmountPlaceholder')}
                  value={productionMinInput}
                  onChange={(event) => setProductionMinInput(event.target.value)}
                  onKeyDown={(e) => { handleProductionKeyDown(e); if (e.key === 'Enter' && productionMetal) setActiveFilterType('') }}
                  type="number"
                  inputProps={{ min: 0 }}
                />
                <TextField
                  sx={{ flex: 1 }}
                  size="small"
                  label={t('projects.filters.annualProductionMaxAmount')}
                  placeholder={t('projects.filters.annualProductionAmountPlaceholder')}
                  value={productionMaxInput}
                  onChange={(event) => setProductionMaxInput(event.target.value)}
                  onKeyDown={(e) => { handleProductionKeyDown(e); if (e.key === 'Enter' && productionMetal) setActiveFilterType('') }}
                  type="number"
                  inputProps={{ min: 0 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">{t('projects.filters.annualProductionHelper')}</Typography>
            </Stack>
          </Box>
        )
      case 'metals':
        return (
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">{t('projects.filters.metals')}</Typography>
              <IconButton size="small" onClick={handleCancelFilter}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={selectedMetals}
                onChange={(e) => { handleMetalChange(e); setActiveFilterType('') }}
                input={<OutlinedInput />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (<Chip key={value} label={t(`projects.metals.${value}`)} size="small" />))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {metals.map((metal) => (<MenuItem key={metal} value={metal}>{t(`projects.metals.${metal}`)}</MenuItem>))}
              </Select>
            </FormControl>
          </Box>
        )
      case 'stages':
        return (
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">{t('projects.filters.stages')}</Typography>
              <IconButton size="small" onClick={handleCancelFilter}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={selectedStages}
                onChange={(e) => { handleStageChange(e); setActiveFilterType('') }}
                input={<OutlinedInput />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (<Chip key={value} label={t(`projects.stages.${value}`)} size="small" />))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {stages.map((stage) => (<MenuItem key={stage} value={stage}>{t(`projects.stages.${stage}`)}</MenuItem>))}
              </Select>
            </FormControl>
          </Box>
        )
      case 'countries':
        return (
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">{t('projects.filters.countries')}</Typography>
              <IconButton size="small" onClick={handleCancelFilter}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            <Autocomplete
              multiple
              options={countryOptions}
              disableCloseOnSelect
              filterSelectedOptions
              value={countryOptions.filter((option) => selectedCountries.includes(option.code))}
              onChange={(_event, newValue, reason: AutocompleteChangeReason) => {
                setSelectedCountries(newValue.map((option) => option.code as IsoCountryCode))
                if (reason === 'selectOption' || reason === 'removeOption' || reason === 'clear') {
                  setCountrySearch('')
                }
              }}
              inputValue={countrySearch}
              onInputChange={(_event, newInputValue, reason) => {
                if (reason === 'input') setCountrySearch(newInputValue)
                if (reason === 'clear') setCountrySearch('')
              }}
              getOptionLabel={(option) => option.label || option.code}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (<Chip {...getTagProps({ index })} key={option.code} label={option.label} size="small" />))
              }
              renderInput={(params) => (
                <TextField {...params} placeholder={t('projects.filters.countryPlaceholder')} size="small" />
              )}
              noOptionsText={t('common.noResults')}
            />
          </Box>
        )
      case 'watchlist':
        return (
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">{t('projects.filters.watchlist')}</Typography>
              <IconButton size="small" onClick={handleCancelFilter}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            <FormControl fullWidth size="small">
              <Select
                value={selectedWatchlist}
                onChange={(e) => { setSelectedWatchlist(e.target.value); setActiveFilterType('') }}
              >
                <MenuItem value=""><em>{t('common.noData')}</em></MenuItem>
                {watchlists.map((watchlist) => (
                  <MenuItem key={watchlist.id} value={watchlist.id}>{watchlist.name} ({watchlist.projectIds.length})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )
      default:
        return null
    }
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <FilterListIcon />
          <Typography variant="h6">{t('projects.filters.title')}</Typography>
        </Box>
        {hasActiveFilters && (
          <Button size="small" onClick={handleClearAll}>{t('projects.filters.clear')}</Button>
        )}
      </Box>

      <Stack spacing={2} sx={{ overflowY: 'auto', flex: 1 }}>
        {/* Favorites Toggle */}
        <FormControlLabel
          control={<Switch checked={favoritesOnly} onChange={(e) => setFavoritesOnly(e.target.checked)} />}
          label={t('projects.filters.favorites')}
        />

        <Divider />

        {/* Active Filters Chips */}
        {hasActiveFilters && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {projectNameFilter && (
              <Chip label={`${t('projects.filters.projectName')}: ${projectNameFilter}`} onDelete={handleProjectNameChipDelete} size="small" />
            )}
            {operatorFilter && (
              <Chip label={`${t('projects.filters.operator')}: ${operatorFilter}`} onDelete={handleOperatorChipDelete} size="small" />
            )}
            {resourceFilter && (
              <Chip
                label={`${t('projects.filters.mineralResource')}: ${t(`projects.metals.${resourceFilter.metal}`)}${resourceFilter.minContent !== undefined ? ` ≥${resourceFilter.minContent.toLocaleString()}` : ''}${resourceFilter.maxContent !== undefined ? ` ≤${resourceFilter.maxContent.toLocaleString()}` : ''} ${resourceFilter.metal === Metal.CU ? 'kt' : 'koz'}`}
                onDelete={handleResourceChipDelete}
                size="small"
              />
            )}
            {productionFilter && (
              <Chip
                label={`${t('projects.filters.annualProduction')}: ${t(`projects.metals.${productionFilter.metal}`)}${productionFilter.minProduction !== undefined ? ` ≥${productionFilter.minProduction.toLocaleString()}` : ''}${productionFilter.maxProduction !== undefined ? ` ≤${productionFilter.maxProduction.toLocaleString()}` : ''} ${productionFilter.metal === Metal.CU ? 'kt' : 'koz'}`}
                onDelete={handleProductionChipDelete}
                size="small"
              />
            )}
            {selectedMetals.map((metal) => (
              <Chip
                key={metal}
                label={`${t('projects.filters.metals')}: ${t(`projects.metals.${metal}`)}`}
                onDelete={() => setSelectedMetals(selectedMetals.filter((m) => m !== metal))}
                size="small"
              />
            ))}
            {selectedStages.map((stage) => (
              <Chip
                key={stage}
                label={`${t('projects.filters.stages')}: ${t(`projects.stages.${stage}`)}`}
                onDelete={() => setSelectedStages(selectedStages.filter((s) => s !== stage))}
                size="small"
              />
            ))}
            {selectedCountries.map((country) => (
              <Chip
                key={country}
                label={`${t('projects.filters.countries')}: ${t(`projects.countries.${country}`)}`}
                onDelete={() => setSelectedCountries(selectedCountries.filter((c) => c !== country))}
                size="small"
              />
            ))}
            {selectedWatchlist && (
              <Chip
                label={`${t('projects.filters.watchlist')}: ${watchlists.find((w) => w.id === selectedWatchlist)?.name}`}
                onDelete={() => setSelectedWatchlist('')}
                size="small"
              />
            )}
          </Box>
        )}

        {/* Add Filter Dropdown */}
        {!activeFilterType && (
          <FormControl fullWidth size="small">
            <InputLabel>{t('projects.filters.addFilter')}</InputLabel>
            <Select
              value=""
              onChange={(e) => setActiveFilterType(e.target.value as FilterType)}
              label={t('projects.filters.addFilter')}
              startAdornment={<AddIcon sx={{ mr: 1, color: 'action.active' }} />}
            >
              {filterTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Dynamic Filter Form */}
        {renderFilterForm()}
      </Stack>
    </Box>
  )
}